import React, { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import classnames from 'classnames'
import moment, { Moment } from 'moment-timezone'
import AddIcon from 'transfix-icons/build/AddIcon'
import { Fab } from 'transfix-ui/components/Fab'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Toast, useToastState } from 'transfix-ui/components/Toast'
import { TopNav } from 'transfix-ui/components/TopNav'
import { makeStyles } from 'transfix-ui/core'
import { $enum } from 'ts-enum-util'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { CreateLoadContainer } from 'apps/fleet-planner/components/create-load/CreateLoadContainer'
import { DriverOptInModal } from 'apps/fleet-planner/components/driver-led-status/DriverOptInModal'
import {
  ShareLoadToastMessage,
  PAGE,
} from 'apps/fleet-planner/components/driver-led-status/ShareLoadToastMessage'
import {
  CalendarView,
  CalendarRow,
  CalendarBar,
} from 'apps/fleet-planner/components/fleet/CalendarView'
import { LoadDetailsModal } from 'apps/fleet-planner/components/load-details/LoadDetailsModal'
import { DeleteLoadModal } from 'apps/fleet-planner/components/modals'
import { ShareLoadModal } from 'apps/fleet-planner/components/share-load/ShareLoadModal'
import { ShareLoadToastWrapper } from 'apps/fleet-planner/components/share-load/ShareLoadToastWrapper'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { ShareLoadProvider } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { obscurePostDeliveryStatus } from 'apps/fleet-planner/pages/loads/utils'
import { ILoad, IDriver, FLEET_RANGE, DriverStatusType } from 'apps/fleet-planner/types'
import { fetchDrivers } from 'apps/fleet-planner/utils/actions/drivers'
import { resetLoadDetails } from 'apps/fleet-planner/utils/actions/loadDetails'
import { fetchLoads } from 'apps/fleet-planner/utils/actions/loads'
import {
  closeDriverOptInModal,
  dismissShareLoadToast,
} from 'apps/fleet-planner/utils/actions/modals'
import {
  selectLoadsByDrivers,
  selectLoadsFetching,
  selectLastCreatedLoad,
  selectLoadsParams,
  selectLoadsError,
  modalInfoSelector,
  selectDrivers,
} from 'apps/fleet-planner/utils/selectors'
import { getFirstStop, getLastStop } from 'apps/fleet-planner/utils/stops'
import { useRefresh } from 'utils/hooks'
import { usePrevious } from 'utils/usePrevious'
import { FORMATS } from 'constants/datetime'
import { DEVICE_INFO } from 'constants/deviceInfo'
import { FleetContextMenu } from './FleetContextMenu'
import { FleetDesktopReminder } from './FleetDesktopReminder'
import { FleetLoad } from './FleetLoad'
import { CalendarRangeSize, FleetTopNav } from './FleetTopNav'
// eslint-disable-next-line perfectionist/sort-imports
import 'apps/fleet-planner/scss/pages/_fleet.scss'

const BAR_HEIGHT = 60
const BAR_HEIGHT_COMPACT = 40

const BAR_HEIGHT_MOBILE = 54
const BAR_HEIGHT_COMPACT_MOBILE = 32

interface IRoute {
  range: FLEET_RANGE
  date: string
  id?: string
  action?: string
}

interface ICreateLoadPayload {
  driver?: IDriver
  range?: [string, string]
}

const useFleetStyles = makeStyles(theme => ({
  firstTopNav: {
    '&.top-nav': {
      [theme.breakpoints.down('md')]: {
        height: 56,
      },
    },
  },
  secondTopNav: {
    '&.top-nav': {
      [theme.breakpoints.down('md')]: {
        height: 48,
        boxShadow: 'none',
      },
    },
  },
  fab: {
    zIndex: 1,
  },
}))

const buildPath = ({ action, date, id, range }: IRoute): string =>
  [
    '/schedule',
    range,
    date,
    id,
    id && action && ['edit', 'delete'].includes(action) ? action : undefined,
  ]
    .filter(Boolean)
    .join('/')

export const Fleet: FC = () => {
  const { trackEvent } = useFMSAnalytics()
  const classes = useFleetStyles()
  const { pathname = '' } = useLocation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { action, date, id, range } = useParams<{
    range: string
    date: string
    id: string
    action: string
  }>()

  const { getFlag } = useFeatureFlagContext()
  const { enabled: isFmsFleetMobileViewEnabled } = getFlag('fmsFleetMobileView')
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
  const { isMobile } = useBreakpointContext()

  const rangeSize = $enum(FLEET_RANGE).asValueOrDefault(
    parseInt(range || '', 10),
    isMobile ? FLEET_RANGE.TWO_DAYS : FLEET_RANGE.THREE_DAYS
  )
  const anchorDate =
    date && moment(date, FORMATS.urlDateFormat).isValid()
      ? moment(date, FORMATS.urlDateFormat)
      : moment()

  if (!range || !date) {
    const currentPath = buildPath({
      range: rangeSize,
      date: anchorDate.format(FORMATS.urlDateFormat),
    })
    history.replace(currentPath)
  }

  const modalLoadId = id === 'create-load' ? undefined : id
  const showEditModal = id === 'create-load' || action === 'edit'
  const showDeleteModal = action === 'delete'

  const pushHistory = (updates: Partial<IRoute>) => {
    const path = buildPath({
      range: rangeSize,
      date: anchorDate.format(FORMATS.urlDateFormat),
      id,
      action,
      ...updates,
    })
    history.push(path)
  }

  const setRangeSize = (newRange: FLEET_RANGE) => pushHistory({ range: newRange })
  const setAnchorDate = (newDate: Moment) =>
    pushHistory({ date: newDate.format(FORMATS.urlDateFormat) })

  const loadDetailsHandler = (load?: ILoad) => {
    pushHistory({ id: load?.id })
    dispatch(resetLoadDetails())
    trackEvent('Load List', 'OPEN_LOAD_DETAILS_PAGE') // TODO: Fix, this events also triggers when the modal is closed
  }

  // We always want to display the unassigned loads as the first driver row
  const [driversWithUnassignedValue, setDriversWithUnassignedValue] = useState<IDriver[]>([])
  const driversList = useSelector(selectDrivers)
  const loadsByDrivers = useSelector(selectLoadsByDrivers)

  const [showDriversNames, setShowDriversNames] = useState<boolean>(false)
  const toggleDriversNames = () => isMobile && setShowDriversNames(!showDriversNames)

  useEffect(() => {
    setDriversWithUnassignedValue([
      { name: 'Unassigned', id: 'unassigned', status: DriverStatusType.active },
      ...driversList,
    ])
  }, [driversList])

  const params = useSelector(selectLoadsParams)
  const fetching = useSelector(selectLoadsFetching)
  const error = useSelector(selectLoadsError)
  const {
    error: modalError,
    actionInProgress: saving,
    isDriverOptInModalOpen,
    drivers = [],
    shareLoads,
  } = useSelector(modalInfoSelector)
  const { message = '', shipmentId = '' } = shareLoads || {}
  const lastCreatedLoad = useSelector(selectLastCreatedLoad)
  const [highlightLoad, setHighlightLoad] = useState<{ id?: string; timestamp?: number }>({})
  const [isDeskstopReminderOpen, setIsDeskstopReminderOpen] = useState(
    DEVICE_INFO.isMobile && !isFmsFleetMobileViewEnabled
  )
  const closeDesktopReminderHandler = () => setIsDeskstopReminderOpen(false)
  const prevHighlightLoad = usePrevious(highlightLoad)
  const navAndCalendarClass = classnames(
    // TopNav and Calendar components need this class applied when the destoptopReminder overlay is open
    { 'fleet-hide-scroll': isDeskstopReminderOpen },
    'top-nav-fleet'
  )

  useEffect(() => {
    if (!highlightLoad.id && prevHighlightLoad?.id) return

    dispatch(fetchDrivers())
    dispatch(
      fetchLoads(
        {
          ...params,
          pageNumber: 0,
          pageSize: 1000,
          startAt: anchorDate
            .clone()
            .subtract(rangeSize === FLEET_RANGE.WEEK ? anchorDate.day() : 0, 'd')
            .startOf('d')
            .toISOString(),
          endAt: anchorDate
            .clone()
            .add(
              rangeSize === FLEET_RANGE.WEEK ? FLEET_RANGE.WEEK - anchorDate.day() : rangeSize,
              'd'
            )
            .startOf('d')
            .toISOString(),
        },
        true
      )
    )
    if (highlightLoad.id && moment().diff(highlightLoad.timestamp, 's') > 1) {
      setHighlightLoad({})
    }
  }, [anchorDate.valueOf(), rangeSize, highlightLoad])

  useEffect(() => {
    if (lastCreatedLoad) {
      setHighlightLoad({
        id: lastCreatedLoad.id,
        timestamp: new Date().valueOf(),
      })
      setAnchorDate(moment(lastCreatedLoad.stops[0].startAt))
    }
  }, [lastCreatedLoad])

  const [createLoadPayload, setCreateLoadPayload] = useState<ICreateLoadPayload>({})
  useEffect(() => {
    if (!showEditModal && createLoadPayload.range) {
      setCreateLoadPayload({})
    }
  }, [showEditModal])

  const [contextMenuTimestamp, setContextMenuTimestamp] = useState<string>()
  const [contextMenuDriver, setContextMenuDriver] = useState<IDriver>()
  const draftLoadEdges: [string, string] = [
    contextMenuTimestamp || '',
    moment(contextMenuTimestamp).add(8, 'h').toISOString() || '',
  ]

  const hideContextMenu = () => {
    setContextMenuTimestamp(undefined)
    setContextMenuDriver(undefined)
  }

  const addLoadHandler = () => {
    trackEvent('Fleet', 'OPEN_ADD_LOAD_MODAL_FV')
    history.push(`${history.location.pathname}/create-load`)
  }

  const calendarRowClickHandler = (timestamp: string, driver: IDriver): void => {
    if (!isMobile) {
      setContextMenuTimestamp(value => (value ? undefined : timestamp))
      setContextMenuDriver(driver)
    }
  }

  const contextMenuHandler = (contextMenuAction: string) => {
    if (contextMenuAction === 'create-load') {
      trackEvent('Fleet', 'OPEN_ADD_LOAD_MODAL_FVDR')
      setCreateLoadPayload({ driver: contextMenuDriver, range: draftLoadEdges })
      history.push(`${history.location.pathname}/create-load`)
    }
  }

  const getLoadEdges = (load: ILoad): [string, string] => {
    const firstStop = getFirstStop(load)
    const lastStop = getLastStop(load)
    return [firstStop.startAt || '', lastStop.endAt || lastStop.startAt || '']
  }

  const getBarClasses = ({ status }: ILoad): string =>
    [
      'fleet-bar',
      status === LOAD_STATE.Pending && 'pending',
      status === LOAD_STATE.Completed && 'completed',
    ]
      .filter(Boolean)
      .join(' ')

  const handleShareLoadClose = () => {
    history.push(pathname.replace('/share', ''))
  }

  const closeOptInModalHandler = () => dispatch(closeDriverOptInModal())
  const closeShareLoadToastHandler = () => dispatch(dismissShareLoadToast())

  // returns true if driver has active loads within the calendar view
  const hasActiveLoads = (driver: IDriver): boolean => {
    if (driver.id) {
      return loadsByDrivers && loadsByDrivers[driver.id]
        ? loadsByDrivers[driver.id].length > 0
        : false
    }
    return false
  }

  /**
   * Re-initializes state of component by changing the key value.
   * This is useful to get a fresh state for Create Load form
   */
  const { key: createModalKey, resetKey } = useRefresh()
  useEffect(() => {
    if (!showEditModal) resetKey()
  }, [resetKey, showEditModal])

  return (
    <>
      {isMobile && (
        <TopNav title='Schedule' className={classnames('top-nav-fleet', classes.firstTopNav)}>
          <CalendarRangeSize rangeSize={rangeSize} onRangeSizeChange={setRangeSize} />
        </TopNav>
      )}
      <FleetTopNav
        className={classnames(navAndCalendarClass, classes.secondTopNav)}
        date={anchorDate}
        rangeSize={rangeSize}
        onRangeSizeChange={setRangeSize}
        onDateChange={setAnchorDate}
      />
      <div className='flex-grow-1 fleet-view-container'>
        {error ? (
          <InlineMessage message={error} type={INLINE_MESSAGE_VARIANTS.ALERT} />
        ) : (
          <CalendarView
            className={navAndCalendarClass}
            startAt={(rangeSize === FLEET_RANGE.WEEK
              ? anchorDate.clone().subtract(anchorDate.day(), 'd')
              : anchorDate
            ).toDate()}
            daysCount={rangeSize}
            showDriversNames={showDriversNames}
            onToggleDriversNames={toggleDriversNames}
            loading={fetching}>
            {driversWithUnassignedValue.map(driver => {
              const driverId = driver.id ?? 'unassigned'
              return (
                <CalendarRow
                  key={driverId}
                  driver={driver}
                  onClick={calendarRowClickHandler}
                  onToggleDriversNames={toggleDriversNames}
                  context={driver.id ? driver : undefined}
                  barHeight={isMobile ? BAR_HEIGHT_MOBILE : BAR_HEIGHT}
                  overlapBarHeight={isMobile ? BAR_HEIGHT_COMPACT_MOBILE : BAR_HEIGHT_COMPACT}
                  hasActiveLoads={hasActiveLoads(driver)}>
                  {loadsByDrivers && // Just in case
                  loadsByDrivers[driverId] && // Until we refactor this
                    loadsByDrivers[driverId].map((load: ILoad) => {
                      const { isExternal, status: loadStatus } = load || {}
                      const status = obscurePostDeliveryStatus(loadStatus, isExternal)

                      const isStatusCancelled = status === LOAD_STATE.Cancelled
                      return (
                        <CalendarBar
                          isVisible={!isStatusCancelled}
                          key={load.id}
                          edges={getLoadEdges(load)}
                          className={getBarClasses(load)}
                          highlight={load.id === highlightLoad.id}>
                          <FleetLoad load={load} onClick={loadDetailsHandler} />
                        </CalendarBar>
                      )
                    })}
                  {Boolean(contextMenuTimestamp && driver.id === contextMenuDriver?.id) && (
                    <FleetContextMenu
                      edges={draftLoadEdges}
                      driver={contextMenuDriver}
                      onHide={hideContextMenu}
                      onAction={contextMenuHandler}
                    />
                  )}
                </CalendarRow>
              )
            })}
          </CalendarView>
        )}
      </div>
      <LoadDetailsModal id={modalLoadId} onClose={loadDetailsHandler} />
      <FleetDesktopReminder isOpen={isDeskstopReminderOpen} onClose={closeDesktopReminderHandler} />
      <CreateLoadContainer
        key={createModalKey}
        showModal={showEditModal}
        error={modalError}
        saving={saving}
        defaultValues={createLoadPayload}
      />
      <DeleteLoadModal
        id={showDeleteModal ? modalLoadId : undefined}
        isFleet
        showDeleteModal={showDeleteModal}
        saving={saving}
        error={modalError}
      />
      {isDriverOptInModalOpen && (
        <DriverOptInModal
          onSuccess={openToast}
          drivers={drivers}
          isOpen={isDriverOptInModalOpen}
          onClose={closeOptInModalHandler}
        />
      )}
      <ShareLoadToastMessage
        page={PAGE.fleet}
        shipmentId={shipmentId}
        message={message}
        onClose={closeShareLoadToastHandler}
      />
      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
      {action === 'share' && (
        <ShareLoadProvider>
          <ShareLoadToastWrapper />
          <ShareLoadModal loadId={modalLoadId} isOpen onClose={handleShareLoadClose} />
        </ShareLoadProvider>
      )}
      {isMobile && isFmsFleetMobileViewEnabled && (
        <Fab
          aria-label='Add load'
          className={classes.fab}
          color='primary'
          size='large'
          onClick={addLoadHandler}>
          <AddIcon />
        </Fab>
      )}
    </>
  )
}

export default Fleet
